"use client";

import { useState } from "react";
import OrderFlow from "../orders/OrderFlow";
import { PAST_PROMO_END_TEXT, PRICES, euro } from "../orders/orderConfig";
import { EXAMPLE_PAGE_HREF } from "../novosti/novostiTexts";

// The colours of the four blocks (from the Cenik design).
const BLOCK = {
  navy: "bg-[#112D77]",
  red: "bg-[#6E0A0A]",
  purple: "bg-[#640F6C]",
  green: "bg-[#105310]",
};
// The texts of a block's rows are in the colour of its headline.
const TEXT = {
  navy: "text-[#112C77]",
  red: "text-[#6D0A0A]",
  purple: "text-[#640F6C]",
  green: "text-[#105310]",
};

// One block of the list: a coloured header and its rows; the header can carry a "Več informacij" toggle.
// example: a link to the example memory page; onExample: instead opens something (the Osmrtnica block opens the popup of
// the highlighted obituary, which is the example).
function Block({ title, tone, info, infoOpen, onToggleInfo, example, onExample, note, plain = false, children }) {
  return (
    <section className="w-full">
      <div className={`${BLOCK[tone]} flex h-[49px] items-center justify-between px-4 text-white`}>
        <h2 className="text-[18px] uppercase leading-[24px] mobile:text-[18px]">{title}</h2>
        {info ?
          <button
            type="button"
            onClick={onToggleInfo}
            aria-expanded={infoOpen}
            className="flex items-center gap-2 text-[14px] text-white/80 hover:text-white"
          >
            <svg viewBox="0 0 12 8" width="14" height="10" aria-hidden="true" className={`transition-transform ${infoOpen ? "rotate-180" : ""}`}>
              <path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="mobile:hidden">Več informacij</span>
            <span className="hidden mobile:inline">info</span>
          </button>
        : null}
        {onExample ?
          <button type="button" onClick={onExample} className="flex items-center gap-2 text-[14px] text-white/80 hover:text-white">
            <svg viewBox="0 0 12 8" width="14" height="10" aria-hidden="true">
              <path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            primer
          </button>
        : example ?
          <a
            href={EXAMPLE_PAGE_HREF}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-[14px] text-white/80 hover:text-white"
          >
            <svg viewBox="0 0 12 8" width="14" height="10" aria-hidden="true">
              <path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            primer
          </a>
        : null}
      </div>
      {info && infoOpen ?
        <div className="bg-[#FBFCFE] px-6 py-5 text-[#414141] shadow-md">{info}</div>
      : null}
      {plain ?
        <div className="mt-2">{children}</div>
      : <div className="border-b-2 border-[#073545] shadow-md">{children}</div>}
      {note ? <p className="mt-2 text-right text-[14px] text-[#6D778E]">{note}</p> : null}
    </section>
  );
}

// A whole row is one button: it opens the order popup of that product. `highlight` is the "Poudarjena osmrtnica" row:
// set apart from the headline, rounded, with the two-colour border (blue to violet) the highlighted obituary has.
function Row({ title, price, free, tone, highlight = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={
        highlight ?
          { background: "linear-gradient(#F7F9E0, #F7F9E0) padding-box, linear-gradient(135deg, #1D8CC2, #5A18C8) border-box", border: "2px solid transparent" }
        : undefined
      }
      className={`flex min-h-[86px] w-full items-center justify-between gap-4 px-[26px] text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A85C2] mobile:min-h-[91px] mobile:px-4 ${TEXT[tone]} ${
        highlight ? "rounded-[12px] shadow-md hover:brightness-[0.98]" : "border-t border-[#E3E8EE] bg-[#FBFCFE] first:border-t-0 hover:bg-white"
      }`}
    >
      <span className="text-[24px] leading-[28px] mobile:text-[20px] mobile:leading-[24px]">{title}</span>
      {free ?
        <span className="text-[14px] uppercase">Brezplačno</span>
      : <span className="whitespace-nowrap text-[40px] font-[300] leading-[44px] mobile:text-[28px]">{price}</span>}
    </button>
  );
}

/**
 * The "Spominske" tab of the Cenik page (new design): four blocks of rows; every row opens the order popup of that
 * product. Memory pages are chosen in Step 0 of the popup (see OrderFlow), so the popups are the same as on a
 * memory page. The prices come from orderConfig (one place for the whole site).
 */
export default function CenikMemorialSection() {
  // { product, preset } of the popup that is open, or null
  const [order, setOrder] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const open = (product, preset = "") => setOrder({ product, preset });

  return (
    <div className="mx-auto flex w-full max-w-[485px] flex-col gap-y-[57px] px-[10px] pb-[72px] pt-4 text-[#414141] mobile:gap-y-[50px] [@media(min-width:744px)]:max-w-[778px] [@media(min-width:744px)]:px-4">
      <Block
        title="Spominska stran"
        tone="navy"
        infoOpen={infoOpen}
        onToggleInfo={() => setInfoOpen((value) => !value)}
        note="Za naročilo klikni vrstico"
        info={
          <div className="space-y-3 text-[15px] leading-[23px]">
            <h3 className="pt-2 text-[17px] text-[#14105F]">10-dnevni skrbnik</h3>
            <p>
              Podarili vam bomo 10-dnevnega skrbnika. Po preteku ga lahko podaljšate ali pa se samodejno prekine. V
              tem primeru vsa že dodana vsebina ostane na spominski strani, dodajanje novih vsebin pa ni več mogoče.
            </p>
            <p>
              Pri 10-dnevnem skrbniku ni spominske svečke in ni omogočena nastavitev preko gesla (zasebna stran);
              omogočeno pa je urejanje strani in sodelovanje vseh bližnjih.
            </p>
            <p className="text-[14px] leading-[22px] text-[#6D778E]">
              Op: Za prevzem skrbništva je treba priložiti dokument o smrti, ki ga izda pogrebno podjetje. S tem
              želimo preprečiti zlorabe, objavljanje neresničnih podatkov in neželene vsebine ter zagotoviti, da je
              skrbništvo zaupano osebi, ki je bila pokojnemu blizu. Ta zahteva je namenjena zaščiti pokojnikovih
              bližnjih, njihovega dostojanstva in varnega ohranjanja spomina.
            </p>
            <div aria-hidden="true" className="h-[22px]" />
          </div>
        }
      >
        <Row tone="navy" title="10 dni" free onClick={() => open("keeper", "free")} />
        <Row tone="navy" title="1 leto" price={euro(PRICES.keeper_1y)} onClick={() => open("keeper", "keeper_1y")} />
        <Row tone="navy" title="5 let" price={euro(PRICES.keeper_5y)} onClick={() => open("keeper", "keeper_5y")} />
      </Block>

      <section className="-mt-[33px] w-full mobile:-mt-[26px]">
        <button
          type="button"
          onClick={() => open("past")}
          className="block w-full border-b-2 border-[#073545] bg-[#E0E9F3] px-[26px] py-4 text-left shadow-md transition hover:bg-[#D8E3F0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A85C2] mobile:px-4"
        >
          <span className="flex items-end justify-between gap-4">
            <span>
              <span className="block text-[13px] uppercase leading-[18px] text-[#6D778E]">Pokopani pred 2026</span>
              <span className="mt-1 block text-[24px] font-semibold leading-[28px] text-[#112D77] mobile:text-[20px]">2-10 let</span>
            </span>
            <span className="whitespace-nowrap text-[36px] font-[300] leading-[40px] text-[#112D77] mobile:text-[26px]">20-30 €</span>
          </span>
          <span className="mt-3 block border-t border-[#C5D2E4] pt-3 text-[12px] leading-[18px] text-[#6D778E]">
            Za bližnje, ki so se poslovili pred letom 2026, podvojimo obdobje trajanja spominske strani do vključno {PAST_PROMO_END_TEXT}. Cena spominske strani za 2 leti znaša {PRICES.past_2y}€ in za 10 let znaša{" "}
            {PRICES.past_10y}€. Po navedenem datumu cene preidejo na običajne.
          </span>
        </button>
      </section>

      <Block title="Topli spomini" tone="red">
        <Row tone="red" title="Zahvala" price={euro(PRICES.thank_you)} onClick={() => open("thank_you")} />
        <Row
          tone="red"
          title={
            <>
              <span className="hidden desktop:inline">Spomin, Obletnica, Zadnje slovo</span>
              <span className="desktop:hidden">Spomin, Zadnje slovo</span>
            </>
          }
          price={euro(PRICES.memory)}
          onClick={() => open("memory")}
        />
      </Block>

      <Block title="Osmrtnica" tone="purple" onExample={() => open("highlight")} plain>
        <Row tone="purple" title="Poudarjena osmrtnica" price={euro(PRICES.highlight)} highlight onClick={() => open("highlight")} />
      </Block>

      <Block title="Spominska svečka" tone="green" example note={null}>
        <Row tone="green" title="1 leto" price={euro(PRICES.candle_1y)} onClick={() => open("candle", "candle_1y")} />
        <Row tone="green" title="5 let" price={euro(PRICES.candle_5y)} onClick={() => open("candle", "candle_5y")} />
      </Block>

      <div className="-mt-[41px] rounded-[8px] bg-[#E1E6EC] p-4 text-[13px] leading-[19px] text-[#6D778E] mobile:-mt-[34px]">
        <span className="inline-block rounded-full bg-white px-2 text-[10px] text-[#7B3FE4]">Spominska svečka</span>
        <strong className="mt-1 flex items-center gap-2 text-[15px] font-normal text-[#1E2125]">
          <span aria-hidden="true" className="inline-flex h-[16px] w-[16px] items-center justify-center rounded-full border border-[#1E2125] text-[10px]">?</span>
          Lahko je tudi darilo bližnjemu
        </strong>
        Spominska svečka je hkrati tudi darilo: poleg svečke in vpisa, podarite obstoječemu skrbniku spominske strani
        hkrati tudi podaljšanje njegovega statusa skrbnika za enako število let.
      </div>

      {order && <OrderFlow product={order.product} preset={order.preset} onClose={() => setOrder(null)} />}
    </div>
  );
}
